"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { CTAButton } from "fansunited-sports-ui";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { BettingOdds } from "@/features/odds/components/BettingOdds";
import { useServerBatchMatchOdds } from "@/features/odds/useServerBatchMatchOdds";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { formatUpcomingMatchTime } from "@/utilities/formatting/date";
import type { MatchRow } from "@/utilities/data/competition/derive/types";
export interface MatchCardListProps {
  /** All matches from the competition */
  matches: MatchRow[];
  /** Maximum upcoming matches to show */
  maxUpcoming?: number;
  /** Maximum results to show */
  maxResults?: number;
}
type TabType = "upcoming" | "results";

/**
 * Single compact match card — teams stacked vertically with optional odds.
 * Designed for sidebar placement.
 */
function MatchCard(t0) {
  const $ = _c(63);
  const {
    match,
    nav,
    prefetchedOdds
  } = t0;
  const {
    hasOdds
  } = useTenantSettings();
  const isFinished = match.status?.type === "FINISHED";
  const isLive = match.status?.type === "LIVE";
  const matchId = match.fansUnitedMatchId || match.id;
  let t1;
  if ($[0] !== match.fansUnitedMatchId || $[1] !== nav) {
    t1 = match.fansUnitedMatchId ? nav.match(match.fansUnitedMatchId) : "#";
    $[0] = match.fansUnitedMatchId;
    $[1] = nav;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const matchHref = t1;
  const kickoffTime = new Date(match.timing.kickoffTime);
  const scoreOne = match.score?.competitorOne ?? 0;
  const scoreTwo = match.score?.competitorTwo ?? 0;
  const [mounted, setMounted] = useState(false);
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setMounted(true);
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const t4 = "py-3";
  const t5 = "flex items-center justify-between mb-2 text-xs text-muted-foreground";
  const t6 = isLive ? <span className="text-success font-semibold">● LIVE</span> : isFinished ? <span>FT</span> : <span>{mounted ? formatUpcomingMatchTime(kickoffTime) : " "}</span>;
  let t7;
  if ($[5] !== match.competition) {
    t7 = match.competition?.name && <span className="truncate ml-2">{match.competition.name}</span>;
    $[5] = match.competition;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t6 || $[8] !== t7) {
    t8 = <div className={t5}>{t6}{t7}</div>;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = match.competitorOne?.assets?.logo;
  const t10 = match.competitorOne?.metadata?.branding?.primaryColor;
  const t11 = match.competitorOne?.metadata?.branding?.secondaryColor;
  const t12 = match.competitorOne?.country?.code;
  const t13 = match.competitorOne?.country?.flag;
  let t14;
  if ($[10] !== t10 || $[11] !== t11 || $[12] !== t12 || $[13] !== t13 || $[14] !== t9) {
    t14 = <TeamLogo src={t9} size="sm" primaryColor={t10} secondaryColor={t11} countryCode={t12} countryFlag={t13} />;
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t13;
    $[14] = t9;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const t15 = match.competitorOne?.name ?? "TBD";
  let t16;
  if ($[16] !== t15) {
    t16 = <span className="text-sm text-card-foreground truncate">{t15}</span>;
    $[16] = t15;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] !== t14 || $[19] !== t16) {
    t17 = <div className="flex items-center gap-2 min-w-0 flex-1">{t14}{t16}</div>;
    $[18] = t14;
    $[19] = t16;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] !== isFinished || $[22] !== isLive || $[23] !== scoreOne) {
    t18 = (isFinished || isLive) && <span className="text-sm font-semibold text-card-foreground">{scoreOne}</span>;
    $[21] = isFinished;
    $[22] = isLive;
    $[23] = scoreOne;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t17 || $[26] !== t18) {
    t19 = <div className="flex items-center justify-between gap-2">{t17}{t18}</div>;
    $[25] = t17;
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  const t20 = match.competitorTwo?.assets?.logo;
  const t21 = match.competitorTwo?.metadata?.branding?.primaryColor;
  const t22 = match.competitorTwo?.metadata?.branding?.secondaryColor;
  const t23 = match.competitorTwo?.country?.code;
  const t24 = match.competitorTwo?.country?.flag;
  let t25;
  if ($[28] !== t20 || $[29] !== t21 || $[30] !== t22 || $[31] !== t23 || $[32] !== t24) {
    t25 = <TeamLogo src={t20} size="sm" primaryColor={t21} secondaryColor={t22} countryCode={t23} countryFlag={t24} />;
    $[28] = t20;
    $[29] = t21;
    $[30] = t22;
    $[31] = t23;
    $[32] = t24;
    $[33] = t25;
  } else {
    t25 = $[33];
  }
  const t26 = match.competitorTwo?.name ?? "TBD";
  let t27;
  if ($[34] !== t26) {
    t27 = <span className="text-sm text-card-foreground truncate">{t26}</span>;
    $[34] = t26;
    $[35] = t27;
  } else {
    t27 = $[35];
  }
  let t28;
  if ($[36] !== t25 || $[37] !== t27) {
    t28 = <div className="flex items-center gap-2 min-w-0 flex-1">{t25}{t27}</div>;
    $[36] = t25;
    $[37] = t27;
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  let t29;
  if ($[39] !== isFinished || $[40] !== isLive || $[41] !== scoreTwo) {
    t29 = (isFinished || isLive) && <span className="text-sm font-semibold text-card-foreground">{scoreTwo}</span>;
    $[39] = isFinished;
    $[40] = isLive;
    $[41] = scoreTwo;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  if ($[43] !== t28 || $[44] !== t29) {
    t30 = <div className="flex items-center justify-between gap-2">{t28}{t29}</div>;
    $[43] = t28;
    $[44] = t29;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  let t31;
  if ($[46] !== t19 || $[47] !== t30) {
    t31 = <div className="px-2 py-1.5 space-y-1">{t19}{t30}</div>;
    $[46] = t19;
    $[47] = t30;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] !== matchHref || $[50] !== t31) {
    t32 = <Link href={matchHref} prefetch={false} className="block hover:bg-muted/50 transition-colors rounded-md">{t31}</Link>;
    $[49] = matchHref;
    $[50] = t31;
    $[51] = t32;
  } else {
    t32 = $[51];
  }
  let t33;
  if ($[52] !== hasOdds || $[53] !== isFinished || $[54] !== isLive || $[55] !== match.timing.kickoffTime || $[56] !== matchId || $[57] !== prefetchedOdds) {
    t33 = !isFinished && hasOdds && <div className="mt-1.5 px-2"><BettingOdds matchId={matchId} markets={["1x2"]} isLive={isLive} prefetchedOdds={prefetchedOdds} market="FT_1X2" kickoffAt={match.timing?.kickoffTime instanceof Date ? match.timing.kickoffTime.toISOString() : match.timing?.kickoffTime} /></div>;
    $[52] = hasOdds;
    $[53] = isFinished;
    $[54] = isLive;
    $[55] = match.timing.kickoffTime;
    $[56] = matchId;
    $[57] = prefetchedOdds;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] !== t32 || $[60] !== t33 || $[61] !== t8) {
    t34 = <div className={t4}>{t8}{t32}{t33}</div>;
    $[59] = t32;
    $[60] = t33;
    $[61] = t8;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  return t34;
}

/**
 * MatchCardList — Compact vertical list of match cards for sidebar placement.
 * Teams stacked vertically with odds. Tabbed upcoming/results.
 */
export const MatchCardList: React.FC<MatchCardListProps> = t0 => {
  const $ = _c(49);
  const {
    matches,
    maxUpcoming: t1,
    maxResults: t2
  } = t0;
  const maxUpcoming = t1 === undefined ? 6 : t1;
  const maxResults = t2 === undefined ? 9 : t2;
  const labels = useLabels();
  const nav = useNavigation();
  const [activeTab, setActiveTab] = useState("upcoming");
  let t3;
  if ($[0] !== matches || $[1] !== maxUpcoming) {
    t3 = matches.filter(_temp).sort(_temp2).slice(0, maxUpcoming);
    $[0] = matches;
    $[1] = maxUpcoming;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const upcoming = t3;
  let t4;
  if ($[3] !== matches || $[4] !== maxResults) {
    t4 = matches.filter(_temp3).sort(_temp4).slice(0, maxResults);
    $[3] = matches;
    $[4] = maxResults;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const finished = t4;
  let t5;
  if ($[6] !== finished || $[7] !== upcoming) {
    t5 = {
      upcomingMatches: upcoming,
      finishedMatches: finished
    };
    $[6] = finished;
    $[7] = upcoming;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    upcomingMatches,
    finishedMatches
  } = t5;
  let t6;
  if ($[9] !== upcomingMatches) {
    t6 = upcomingMatches.map(_temp5);
    $[9] = upcomingMatches;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const oddsMatchIds = t6;
  let t7;
  if ($[11] !== upcomingMatches) {
    t7 = upcomingMatches.some(_temp6);
    $[11] = upcomingMatches;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const hasLiveMatches = t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = ["1x2"];
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== hasLiveMatches || $[15] !== oddsMatchIds) {
    t9 = {
      matchIds: oddsMatchIds,
      markets: t8,
      isLive: hasLiveMatches
    };
    $[14] = hasLiveMatches;
    $[15] = oddsMatchIds;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const {
    oddsMap: batchOddsMap
  } = useServerBatchMatchOdds(t9);
  const hasUpcoming = upcomingMatches.length > 0;
  const hasResults = finishedMatches.length > 0;
  if (!hasUpcoming && !hasResults) {
    return null;
  }
  const activeMatches = activeTab === "upcoming" ? upcomingMatches : finishedMatches;
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setActiveTab("upcoming");
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = `px-3 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer ${activeTab === "upcoming" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`;
  let t12;
  if ($[18] !== labels.homePage.upcomingMatchesMobile || $[19] !== t11) {
    t12 = <button onClick={t10} className={t11}>{labels.homePage.upcomingMatchesMobile}</button>;
    $[18] = labels.homePage.upcomingMatchesMobile;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setActiveTab("results");
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = `px-3 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer ${activeTab === "results" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`;
  let t15;
  if ($[22] !== labels.homePage.latestResultsMobile || $[23] !== t14) {
    t15 = <button onClick={t13} className={t14}>{labels.homePage.latestResultsMobile}</button>;
    $[22] = labels.homePage.latestResultsMobile;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t12 || $[26] !== t15) {
    t16 = <div className="flex gap-2">{t12}{t15}</div>;
    $[25] = t12;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const t17 = labels.homePage.allFixturesMobile;
  let t18;
  if ($[28] !== nav) {
    t18 = nav.matchesList();
    $[28] = nav;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      container: "text-xs font-medium text-accent hover:text-accent/80 border-none p-0"
    };
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== labels.homePage.allFixturesMobile || $[32] !== t18) {
    t20 = <CTAButton text={t17} href={t18} variant="custom" styles={t19} />;
    $[31] = labels.homePage.allFixturesMobile;
    $[32] = t18;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t16 || $[35] !== t20) {
    t21 = <div className="flex items-center justify-between mb-3">{t16}{t20}</div>;
    $[34] = t16;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== activeMatches || $[38] !== activeTab || $[39] !== batchOddsMap || $[40] !== labels.homePage.noRecentResults || $[41] !== labels.homePage.noUpcomingMatches || $[42] !== nav) {
    t22 = activeMatches.length > 0 ? activeMatches.map(match => <MatchCard key={match.id} match={match} nav={nav} prefetchedOdds={batchOddsMap} />) : <p className="text-sm text-muted-foreground py-4 text-center">{activeTab === "upcoming" ? labels.homePage.noUpcomingMatches : labels.homePage.noRecentResults}</p>;
    $[37] = activeMatches;
    $[38] = activeTab;
    $[39] = batchOddsMap;
    $[40] = labels.homePage.noRecentResults;
    $[41] = labels.homePage.noUpcomingMatches;
    $[42] = nav;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t22) {
    t23 = <div className="bg-card text-card-foreground divide-y divide-border">{t22}</div>;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== t21 || $[47] !== t23) {
    t24 = <section>{t21}{t23}</section>;
    $[46] = t21;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  return t24;
};
function _temp(m) {
  const status = m.status?.type;
  return status === "LIVE" || status === "NOT_STARTED" || status === undefined;
}
function _temp2(a, b) {
  return new Date(a.timing.kickoffTime).getTime() - new Date(b.timing.kickoffTime).getTime();
}
function _temp3(m_0) {
  return m_0.status?.type === "FINISHED";
}
function _temp4(a_0, b_0) {
  return new Date(b_0.timing.kickoffTime).getTime() - new Date(a_0.timing.kickoffTime).getTime();
}
function _temp5(m_1) {
  return m_1.fansUnitedMatchId || m_1.id;
}
function _temp6(m_2) {
  return m_2.status?.type === "LIVE";
}